<template>
  <a-modal
    :open="open"
    class="Modal__Highlights"
    :footer="null"
    :destroy-on-close="true"
    :width="'5.5rem'"
  >
    <div class="wrap">
      <div class="video">
        <VideoPlayer
          :url="props.data?.preview_m3u8_url || props.data?.video_url"
          :poster="transformImage(props.data?.video_cover_url)"
          class="mr-[20px] video-player"
          type="template"
        />
      </div>
      <div class="container">
        <div class="top-content">
          <div class="title flex items-center">
            <div>{{ props.data?.title }}</div>
            <IconStar filled />
          </div>
          <div class="author flex items-center mt-[4px]">
            <Resource
              class="avatar mr-[5px]"
              :src="
                getBeUrl(
                  isChinese()
                    ? '/youyan/images/default/youyan.png'
                    : '/youyan/images/default/rendora_studio.jpeg'
                )
              "
              :transformResource="transformImage"
            ></Resource>
            <a-button type="text" @click="handClick">
              {{ $t('xmov-studio-you-yan-gong-zuo-shi') }}
            </a-button>
          </div>
          <a-button
            class="go"
            :loading="pending"
            @click="goToEditorWithAnchor(props.data)"
          >
            {{ userInfo ? $t('kuai-qu-ti-yan') : $t('mian-fei-zhu-ce-ti-yan') }}
            <IconArrowRight class="ml-[5px]" filled />
          </a-button>
        </div>
        <div class="divider" />
        <div class="bottom-content">
          {{ props.data?.description }}
        </div>
      </div>
    </div>
  </a-modal>
</template>
<script lang="ts" setup>
import type { IProductHighlight } from '~/api/interface/publicize'
import VideoPlayer from '~/components/videoPlayer/index.vue'

import IconStar from '~/assets/svg/star.svg?component'
import IconArrowRight from '~/assets/svg/arrow-right.svg?component'
import { duplicateOpus } from '~/api'
import { PageKey } from '~/constants/page'

const userInfo = useUserInfo()
const open = defineModel<boolean>()
const props = defineProps<{ data?: IProductHighlight }>()
const cfg = useRuntimeConfig()
const pending = ref<boolean>()
const goToEditorWithAnchor = async (highlight?: IProductHighlight) => {
  trackEvent(TRACK_EVENT.CLICK_HIGHLIGHTS_EXPERIENCE_NOW, {
    title: highlight?.title,
    subtitle: highlight?.card_subtitle,
    is_logged_on: !!userInfo.value,
  })

  if (!highlight) {
    return
  }

  if (pending.value) {
    return
  }

  const route = useRoute()
  pending.value = true

  let link: string = ''

  if (
    ['actor', 'common', 'sliding_block_edit'].includes(highlight.click_jump_to)
  ) {
    const action = {
      type: IAfterLoginActionType.USE_TEMPLATE,
      payload: [
        { id: highlight.template_id },
        `intro=${highlight?.click_jump_to}`,
      ],
    }
    loginTask.add(action)
    const res = await duplicateOpus(highlight.template_id)
    loginTask.remove(action)
    if (res.data) {
      link = `/editor/${res.data.id}/${res.data.secret_id}`
    }
  } else {
    link = `/editor/${highlight?.template_id}/${highlight?.secret_id}`
  }

  if (highlight?.click_jump_to) {
    link += `?intro=${highlight?.click_jump_to}`
  }

  trackEvent(TRACK_EVENT.启动编辑器, {
    source: TEMPLATE_SOURCE.HIGHLIGHTS,
    feature: highlight?.title,
    page: route.name,
  })

  pending.value = false

  window.location.href = link
}

const handClick = () => {
  let key = 183
  if (['production', 'pre'].includes(cfg.public.env)) {
    key = 2
  }
  if (props.data?.editor_id) {
    const router = useRouter()
    router.push({
      name: PageKey.PERSONAL_PAGE,
      params: {
        key,
      },
    })
    const open = useTemplateVisible()
    open.value = false
  }
}

watch(
  () => open.value,
  (val) => {
    if (val) {
      trackEvent(TRACK_EVENT.PLAY_HIGHLIGHTS_VIDEO, {
        title: props.data?.title,
      })
    }
  }
)
</script>
<style lang="scss" global>
.Modal__Highlights {
  .ant-modal-content {
    width: rem(704, 1920);
    padding: rem(15, 1920) rem(30, 1920) 0 0;
    background: none;
    box-shadow: none;
  }
  .ant-modal-close {
    position: absolute;
    right: 0;
    top: 0;
    color: #fff;
  }
}
</style>
<style lang="scss" scoped>
.Modal__Highlights {
  .video-player {
    position: absolute;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
    border-radius: 0;

    :deep(video) {
      border-radius: 0px !important;
    }

    :deep(.vjs-control-bar) {
      border-bottom-left-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  .bottom-content {
    font-weight: 400;
    font-size: rem(14, 1920);
    color: var(--text-1-40);
    line-height: rem(22, 1920);
    padding: 0 rem(30, 1920) rem(25, 1920);
  }
  .divider {
    border-top: 1px solid var(--back-4);
    margin: 0 auto rem(11, 1920);
    width: rem(644, 1920);
  }
  .wrap {
    background-color: var(--back-1);
    border-radius: rem(20, 1920);
    overflow: hidden;
  }
  .video {
    position: relative;
    padding-top: 56.25%;
    background-color: black;
  }
  .top-content {
    position: relative;
    padding: rem(26, 1920) rem(30, 1920) rem(18, 1920);
  }
  .title {
    > div {
      font-weight: 500;
      font-size: rem(20, 1920);
      color: var(--text-1);
      line-height: rem(29, 1920);
      white-space: nowrap;
      overflow: hidden;
      max-width: 2.8rem;
      text-overflow: ellipsis;
    }
  }
  .author {
    cursor: pointer;
    font-weight: 400;
    font-size: rem(14, 1920);
    color: var(--text-1-60);
    line-height: rem(18, 1920);
  }
  .avatar {
    height: rem(20);
    width: rem(20);
    border-radius: 50%;
  }
  .go {
    cursor: pointer;
    position: absolute;
    top: rem(28, 1920);
    right: rem(30, 1920);
    height: rem(44, 1920);
    background: var(--purple-6);
    box-shadow: 0px 4px 14px 0px rgba(105, 98, 255, 0.35);
    border-radius: rem(10, 1920);
    font-weight: 500;
    font-size: rem(16, 1920);
    color: #ffffff;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>
